import { defineStore } from 'pinia'
import { ref, watch } from 'vue'

export type ThemePreference = 'light' | 'dark' | 'auto'
type ResolvedTheme = 'light' | 'dark'

const STORAGE_KEY = 'mts_ai_theme'

function resolve(pref: ThemePreference): ResolvedTheme {
  if (pref !== 'auto') return pref
  const mq = window.matchMedia('(prefers-color-scheme: dark)')
  return mq.matches ? 'dark' : 'light'
}

export const useThemeStore = defineStore('theme', () => {
  const preference = ref<ThemePreference>('auto')
  const resolved = ref<ResolvedTheme>('light')

  function apply(theme: ResolvedTheme): void {
    resolved.value = theme
    document.documentElement.dataset.theme = theme
  }

  function setPreference(pref: ThemePreference): void {
    preference.value = pref
    localStorage.setItem(STORAGE_KEY, pref)
    apply(resolve(pref))
  }

  function init(): void {
    const stored = localStorage.getItem(STORAGE_KEY) as ThemePreference | null
    preference.value = stored ?? 'auto'
    apply(resolve(preference.value))

    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    mq.addEventListener('change', () => {
      if (preference.value === 'auto') apply(resolve('auto'))
    })
  }

  watch(preference, (pref) => {
    apply(resolve(pref))
  })

  return { preference, resolved, setPreference, init }
})
